import React, { useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import {
  COMPLAINT_STATUS,
  getRoleLabel,
  getStatusLabel,
  getStatusBadgeClass,
  resolveUserName,
} from "../../utils/complaintHelpers";
import { formatPenaltyDays } from "../../utils/penaltyFormat";
import { filterHrPenaltyList } from "../../utils/penaltyMonthHelpers";
import {
  collectCurrentlyOnLeaveMatchers,
  collectCurrentlyOnWeeklyRestMatchers,
  employeeIsCurrentlyOff,
  employeeIsCurrentlyOnLeave,
  employeeIsCurrentlyOnWeeklyRest,
  findCurrentLeaveForEmployee,
  todayDateOnlyIso,
} from "../../utils/currentLeaveHelpers";
import AppealSubjectDetails from "../common/AppealSubjectDetails";
import AppealDeadlineClock from "../common/AppealDeadlineClock";
import HomeAttendanceClock from "../employee/HomeAttendanceClock";
import LiveAttendanceBoard from "../common/LiveAttendanceBoard";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { isNavItemVisible } from "../../utils/featureVisibility";
import { fieldCompleteness } from "../../utils/profileCompleteness";
import { getAppealLevelLabel } from "../../utils/appealCycle";
import "./HrDashboard.css";

const TABS = [
  { id: "overview", label: "نظرة عامة" },
  { id: "leaves", label: "الإجازات والطلبات" },
  { id: "penalties", label: "الجزاءات" },
  { id: "complaints", label: "الشكاوى" },
  { id: "appeals", label: "التظلمات" },
];

const CREATOR_ROLE_LABELS = {
  manager: "مدير فرع",
  "department-manager": "مدير إدارة",
  hr: "موارد بشرية",
};

const REQUEST_STATUS_LABELS = {
  pending: "معلق",
  approved: "موافق عليه",
  rejected: "مرفوض",
};

function isLeaveRequest(type) {
  return (
    type?.includes("إجازة") || type === "إذن" || type?.includes("مرضية")
  );
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("ar-EG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function RequestStatusBadge({ status }) {
  const cls =
    status === "approved"
      ? "approved"
      : status === "rejected"
        ? "rejected"
        : "pending";
  return (
    <span className={`hr-badge ${cls}`}>
      {REQUEST_STATUS_LABELS[status] || status}
    </span>
  );
}

function PenaltyStatusBadge({ penalty }) {
  if (penalty.status === "appealed") {
    return (
      <div className="ess-penalty-status-stack">
        <span className="hr-badge appealed">
          {penalty.appealStatus || "معلق"}
        </span>
        {penalty.appealDueAt ? (
          <AppealDeadlineClock dueAt={penalty.appealDueAt} compact />
        ) : null}
      </div>
    );
  }
  return <span className="hr-badge active">ساري</span>;
}

function AppealStatusBadge({ appeal }) {
  if (appeal.finalStatus === "approved") {
    return <span className="hr-badge approved">مقبول نهائياً</span>;
  }
  if (appeal.finalStatus === "rejected") {
    return <span className="hr-badge rejected">مرفوض نهائياً</span>;
  }
  const level = appeal.currentLevel;
  const levelKey =
    level === "department-manager" ? "departmentManager" : level;
  const levelStatus = appeal.levels?.[levelKey]?.status;
  if (levelStatus === "pending") {
    return (
      <span className="hr-badge pending">
        بانتظار {getAppealLevelLabel(levelKey, appeal) || level}
      </span>
    );
  }
  return <span className="hr-badge in-progress">قيد المراجعة</span>;
}

function buildEmployeeStatusMap(
  employeesByBranch,
  pendingRequests,
  complaints,
  allPenaltiesList,
  appeals,
  timesheets,
) {
  const allEmps = Object.values(employeesByBranch).flat();
  const map = new Map();
  allEmps.forEach((emp) => map.set(emp.id, { emp, tags: [] }));

  const userToEmpId = new Map(
    allEmps.filter((e) => e.userId).map((e) => [e.userId, e.id]),
  );

  const leaveMatchers = collectCurrentlyOnLeaveMatchers(pendingRequests);
  const restMatchers = collectCurrentlyOnWeeklyRestMatchers(timesheets);
  allEmps
    .filter((e) => employeeIsCurrentlyOnLeave(e, leaveMatchers))
    .forEach((emp) => {
      map.get(emp.id)?.tags.push({ key: "leave", icon: "🏖️", label: "في إجازة", cls: "tag-leave" });
    });
  allEmps
    .filter(
      (e) =>
        !employeeIsCurrentlyOnLeave(e, leaveMatchers) &&
        employeeIsCurrentlyOnWeeklyRest(e, restMatchers),
    )
    .forEach((emp) => {
      map.get(emp.id)?.tags.push({
        key: "rest",
        icon: "🛌",
        label: "راحة أسبوعية",
        cls: "tag-rest",
      });
    });

  allEmps
    .filter((e) => (e.advance?.outstandingAmount ?? 0) > 0)
    .forEach((emp) => {
      map.get(emp.id)?.tags.push({ key: "advance-active", icon: "💳", label: "سلفة نشطة", cls: "tag-advance" });
    });

  pendingRequests
    .filter((r) => (r.type === "سلفة" || r.type?.includes("سلف")) && r.status === "pending")
    .forEach((req) => {
      const empId = userToEmpId.get(req.userId);
      const entry = empId ? map.get(empId) : null;
      if (entry && !entry.tags.find((t) => t.key === "advance-pending")) {
        entry.tags.push({ key: "advance-pending", icon: "💰", label: "سلفة معلقة", cls: "tag-advance" });
      }
    });

  const openStatuses = ["Pending", "In Progress"];
  complaints
    .filter((c) => openStatuses.includes(c.status))
    .forEach((c) => {
      const entry = c.senderId ? map.get(c.senderId) : null;
      if (entry && !entry.tags.find((t) => t.key === "complaint")) {
        entry.tags.push({ key: "complaint", icon: "📢", label: "شكوى مفتوحة", cls: "tag-complaint" });
      }
    });

  allPenaltiesList
    .filter((p) => p.status === "active")
    .forEach((pen) => {
      const entry = pen.employeeId ? map.get(pen.employeeId) : null;
      if (entry && !entry.tags.find((t) => t.key === "penalty")) {
        entry.tags.push({ key: "penalty", icon: "⚠️", label: "جزاء ساري", cls: "tag-penalty" });
      }
    });

  appeals
    .filter((a) => !a.finalStatus)
    .forEach((a) => {
      const empId = a.employeeId;
      const entry = empId ? map.get(empId) : null;
      if (entry && !entry.tags.find((t) => t.key === "appeal")) {
        entry.tags.push({ key: "appeal", icon: "⚖️", label: "تظلم معلق", cls: "tag-appeal" });
      }
    });

  return Array.from(map.values()).filter(
    (item) => item.tags.length > 0 && item.emp.status !== "terminated",
  );
}

const EMPLOYEE_STATUS_SECTIONS = [
  { key: "advance-active", title: "السلف النشطة" },
  { key: "advance-pending", title: "السلف المعلقة" },
  { key: "leave", title: "الإجازات الحالية" },
  { key: "rest", title: "راحة أسبوعية اليوم" },
  { key: "complaint", title: "الشكاوى المفتوحة" },
  { key: "penalty", title: "الجزاءات السارية" },
  { key: "appeal", title: "التظلمات المعلقة" },
];

function HrDashboard() {
  const {
    branches,
    employeesByBranch,
    pendingRequests,
    leaveCalendarData,
    timesheets,
    allPenalties,
    complaints,
    appeals,
    recentActivities,
    getEmployeeById,
    getHrStats,
    getAllEmployees,
  } = useData();

  const [activeTab, setActiveTab] = useState("overview");
  const [expandedStatusSections, setExpandedStatusSections] = useState({});
  const flags = useFeatureFlags();
  const tabs = useMemo(
    () => TABS.filter((tab) => isNavItemVisible(flags, tab.id)),
    [flags],
  );

  const stats = getHrStats();
  const incompleteProfiles = useMemo(
    () =>
      getAllEmployees().filter(
        (emp) =>
          emp.status !== "terminated" &&
          !fieldCompleteness(emp.profileCompleteness).isComplete,
      ).length,
    [getAllEmployees],
  );

  const allPenaltiesList = useMemo(
    () => allPenalties,
    [allPenalties],
  );
  const recentPenaltiesList = useMemo(
    () => filterHrPenaltyList(allPenaltiesList),
    [allPenaltiesList],
  );

  const leaveRequests = useMemo(
    () => pendingRequests.filter((r) => isLeaveRequest(r.type)),
    [pendingRequests],
  );

  const employeeStatusList = useMemo(
    () => buildEmployeeStatusMap(employeesByBranch, pendingRequests, complaints, allPenaltiesList, appeals, timesheets),
    [employeesByBranch, pendingRequests, complaints, allPenaltiesList, appeals, timesheets],
  );

  const employeeStatusSections = useMemo(
    () =>
      EMPLOYEE_STATUS_SECTIONS.map((section) => ({
        ...section,
        items: employeeStatusList.filter(({ tags }) =>
          tags.some((tag) => tag.key === section.key),
        ),
      })).filter((section) => section.items.length > 0),
    [employeeStatusList],
  );

  const leaveMatchers = useMemo(
    () => collectCurrentlyOnLeaveMatchers(pendingRequests),
    [pendingRequests],
  );
  const restMatchers = useMemo(
    () => collectCurrentlyOnWeeklyRestMatchers(timesheets),
    [timesheets],
  );

  const currentlyOnLeaveRows = useMemo(() => {
    return Object.entries(employeesByBranch).flatMap(([branchId, emps]) => {
      const branch = branches.find((b) => b.id === branchId);
      return emps
        .filter((e) => employeeIsCurrentlyOff(e, leaveMatchers, restMatchers))
        .map((emp) => ({
          emp,
          branch,
          leave:
            findCurrentLeaveForEmployee(emp, pendingRequests) ||
            (employeeIsCurrentlyOnWeeklyRest(emp, restMatchers)
              ? {
                  type: "راحة أسبوعية",
                  startDate: todayDateOnlyIso(),
                  endDate: todayDateOnlyIso(),
                  _source: "weekly-rest",
                }
              : null),
        }));
    });
  }, [employeesByBranch, branches, leaveMatchers, restMatchers, pendingRequests]);

  const branchSummaries = useMemo(() => {
    return branches.map((branch) => {
      const employees = employeesByBranch[branch.id] || [];
      return {
        ...branch,
        total: employees.length,
        active: employees.filter((e) => e.status === "active").length,
        onLeave: employees.filter((e) =>
          employeeIsCurrentlyOff(e, leaveMatchers, restMatchers),
        ).length,
      };
    });
  }, [branches, employeesByBranch, leaveMatchers, restMatchers]);

  const timeline = useMemo(() => {
    const items = [];

    pendingRequests.forEach((req) => {
      items.push({
        id: `req-${req.id}`,
        icon: isLeaveRequest(req.type) ? "🏖️" : "📋",
        text: `${req.employeeName} — ${req.type}`,
        meta: `${REQUEST_STATUS_LABELS[req.status] || req.status} · ${formatDate(req.submittedAt)}`,
        date: new Date(req.submittedAt || 0),
        category: isLeaveRequest(req.type) ? "leaves" : "requests",
      });
    });

    recentPenaltiesList.forEach((pen) => {
      const name = resolveUserName(pen.employeeId, getEmployeeById);
      items.push({
        id: `pen-${pen.id}`,
        icon: "⚠️",
        text: `جزاء ${pen.type} — ${name}`,
        meta: `${pen.status === "appealed" ? pen.appealStatus || "معلق" : "ساري"} · ${pen.date}`,
        date: new Date(),
        category: "penalties",
      });
    });

    complaints.forEach((c) => {
      items.push({
        id: `cmp-${c.id}`,
        icon: "📢",
        text: `شكوى: ${c.title} — ${c.senderName}`,
        meta: `${getStatusLabel(c.status)} · ${formatDate(c.createdAt)}`,
        date: new Date(c.createdAt),
        category: "complaints",
      });
    });

    appeals.forEach((a) => {
      items.push({
        id: `apl-${a.id}`,
        icon: "⚖️",
        text: `تظلم — ${a.employeeName}`,
        meta: `${a.finalStatus ? (a.finalStatus === "approved" ? "مقبول" : "مرفوض") : "قيد المراجعة"} · ${formatDate(a.submittedAt)}`,
        date: new Date(a.submittedAt),
        category: "appeals",
      });
    });

    recentActivities.forEach((act) => {
      items.push({
        id: `act-${act.id}`,
        icon:
          act.type === "leave"
            ? "🏖️"
            : act.type === "advance"
              ? "💰"
              : act.type === "sick"
                ? "🏥"
                : act.type === "permit"
                  ? "⏰"
                  : act.type === "appeal"
                    ? "⚖️"
                    : "📌",
        text: act.text,
        meta: act.date,
        date: new Date(),
        category: "overview",
      });
    });

    return items
      .sort((a, b) => b.date - a.date)
      .slice(0, 12);
  }, [
    pendingRequests,
    recentPenaltiesList,
    complaints,
    appeals,
    recentActivities,
    getEmployeeById,
  ]);

  const tabBadges = {
    leaves: stats.pendingLeaveRequests,
    penalties: stats.pendingHrPenalties,
    complaints: stats.openComplaints,
    appeals: stats.hrPendingAppeals,
  };

  function toggleStatusSection(sectionKey) {
    setExpandedStatusSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }

  function renderEmployeeStatusPanel() {
    if (employeeStatusSections.length === 0) return null;
    return (
      <div className="hr-section hr-emp-status-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">حالات الموظفين النشطة</h2>
          <span className="hr-section-count">{employeeStatusList.length} موظف</span>
        </div>
        <div className="hr-status-accordion">
          {employeeStatusSections.map((section) => {
            const isExpanded = Boolean(expandedStatusSections[section.key]);
            return (
              <div key={section.key} className="hr-status-group">
                <button
                  type="button"
                  className={`hr-status-toggle${isExpanded ? " is-open" : ""}`}
                  onClick={() => toggleStatusSection(section.key)}
                >
                  <span>{section.title}</span>
                  <span className="hr-status-toggle-meta">
                    <span className="hr-section-count">{section.items.length} موظف</span>
                    <span className="hr-status-toggle-icon">
                      {isExpanded ? "−" : "+"}
                    </span>
                  </span>
                </button>

                {isExpanded && (
                  <div className="hr-emp-status-grid">
                    {section.items.map(({ emp, tags }) => {
                      const branch = branches.find((b) => b.id === emp.branchId);
                      return (
                        <div key={`${section.key}-${emp.id}`} className="hr-emp-status-card">
                          <div className="hr-emp-status-header">
                            <span className="hr-emp-status-name">{emp.name}</span>
                            <span className="hr-role-tag">{emp.code}</span>
                          </div>
                          {emp.jobTitle && (
                            <span className="hr-emp-status-title">{emp.jobTitle}</span>
                          )}
                          {branch && (
                            <span className="hr-emp-status-branch">🏢 {branch.name}</span>
                          )}
                          <div className="hr-emp-tags">
                            {tags
                              .filter((tag) => tag.key === section.key)
                              .map((tag) => (
                                <span key={tag.key} className={`hr-emp-tag ${tag.cls}`}>
                                  {tag.icon} {tag.label}
                                </span>
                              ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderOverview() {
    return (
      <>
        <LiveAttendanceBoard getEmployeeById={getEmployeeById} />
        {renderEmployeeStatusPanel()}
        <div className="hr-two-col">
          <div className="hr-section">
            <div className="hr-section-header">
              <h2 className="hr-section-title">آخر النشاطات</h2>
              <span className="hr-section-count">{timeline.length} حدث</span>
            </div>
            <div className="hr-timeline">
              {timeline.length === 0 ? (
                <p className="hr-empty">لا توجد نشاطات حديثة</p>
              ) : (
                timeline.map((item) => (
                  <div key={item.id} className="hr-timeline-item">
                    <span className="hr-timeline-icon">{item.icon}</span>
                    <div className="hr-timeline-body">
                      <span className="hr-timeline-text">{item.text}</span>
                      <span className="hr-timeline-meta">{item.meta}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="hr-section">
            <div className="hr-section-header">
              <h2 className="hr-section-title">الفروع</h2>
              <span className="hr-section-count">
                {stats.totalBranches} فرع
              </span>
            </div>
            <div className="hr-branches-grid">
              {branchSummaries.map((branch) => (
                <div key={branch.id} className="hr-branch-card">
                  <div className="hr-branch-name">🏢 {branch.name}</div>
                  <div className="hr-branch-stats">
                    <span>👥 {branch.total}</span>
                    <span>✅ {branch.active}</span>
                    <span>🏖️ {branch.onLeave}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">ملخص سريع</h2>
          </div>
          <div className="hr-table-wrap">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>القسم</th>
                  <th>المعلق</th>
                  <th>النشط</th>
                  <th>الإجمالي</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>طلبات الإجازة والأذونات</td>
                  <td>{stats.pendingLeaveRequests}</td>
                  <td>{leaveCalendarData.length} في التقويم</td>
                  <td>{leaveRequests.length}</td>
                </tr>
                <tr>
                  <td>الجزاءات</td>
                  <td>{stats.appealedPenalties} متظلم</td>
                  <td>{stats.activePenalties} ساري</td>
                  <td>{stats.totalPenalties}</td>
                </tr>
                <tr>
                  <td>الشكاوى</td>
                  <td>{stats.openComplaints}</td>
                  <td>
                    {
                      complaints.filter(
                        (c) => c.status === COMPLAINT_STATUS.RESOLVED,
                      ).length
                    }{" "}
                    محلولة
                  </td>
                  <td>{stats.totalComplaints}</td>
                </tr>
                <tr>
                  <td>التظلمات</td>
                  <td>{stats.hrPendingAppeals} بانتظار HR</td>
                  <td>{stats.allPendingAppeals} قيد المراجعة</td>
                  <td>{appeals.length}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  function renderLeaves() {
    return (
      <>
        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">طلبات الإجازات والأذونات</h2>
            <span className="hr-section-count">
              {leaveRequests.length} طلب
            </span>
          </div>
          <div className="hr-table-wrap">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>الموظف</th>
                  <th>النوع</th>
                  <th>التفاصيل</th>
                  <th>السبب</th>
                  <th>تاريخ التقديم</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {leaveRequests.map((req) => (
                  <tr key={req.id}>
                    <td>
                      <div>{req.employeeName}</div>
                      <span className="hr-role-tag">{req.employeeCode}</span>
                    </td>
                    <td>{req.type}</td>
                    <td>
                      {req.days
                        ? `${req.days} يوم`
                        : req.hours
                          ? `${req.hours} ساعة`
                          : req.startDate || "—"}
                    </td>
                    <td>{req.reason || "—"}</td>
                    <td>{formatDate(req.submittedAt)}</td>
                    <td>
                      <RequestStatusBadge status={req.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {leaveRequests.length === 0 && (
              <p className="hr-empty">لا توجد طلبات إجازة</p>
            )}
          </div>
        </div>

        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">تقويم الإجازات القادمة</h2>
            <span className="hr-section-count">
              {leaveCalendarData.length} إدخال
            </span>
          </div>
          <div className="hr-table-wrap">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>الموظف</th>
                  <th>النوع</th>
                  <th>المدة</th>
                </tr>
              </thead>
              <tbody>
                {leaveCalendarData.map((entry, idx) => (
                  <tr key={`${entry.date}-${entry.employee}-${idx}`}>
                    <td>{formatDate(entry.date)}</td>
                    <td>{entry.employee}</td>
                    <td>{entry.type}</td>
                    <td>
                      {entry.days
                        ? `${entry.days} يوم`
                        : entry.hours
                          ? `${entry.hours} ساعة`
                          : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">موظفون في إجازة أو راحة حالياً</h2>
            <span className="hr-section-count">
              {currentlyOnLeaveRows.length} موظف
            </span>
          </div>
          <div className="hr-table-wrap">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>الكود</th>
                  <th>المسمى</th>
                  <th>الفرع</th>
                  <th>النوع</th>
                  <th>المدة</th>
                </tr>
              </thead>
              <tbody>
                {currentlyOnLeaveRows.map(({ emp, branch, leave }) => (
                  <tr key={emp.id}>
                    <td>{emp.name}</td>
                    <td>{emp.code}</td>
                    <td>{emp.jobTitle}</td>
                    <td>{branch?.name || emp.branchName || emp.branchId}</td>
                    <td>{leave?.type || "إجازة"}</td>
                    <td>
                      {leave?.startDate
                        ? `${formatDate(leave.startDate)} — ${formatDate(leave.endDate || leave.startDate)}`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {currentlyOnLeaveRows.length === 0 && (
              <p className="hr-empty">لا يوجد موظفون في إجازة أو راحة حالياً</p>
            )}
          </div>
        </div>
      </>
    );
  }

  function renderPenalties() {
    return (
      <div className="hr-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">جميع الجزاءات</h2>
          <span className="hr-section-count">
            {recentPenaltiesList.length} جزاء خلال آخر شهرين
          </span>
        </div>
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>نوع الجزاء</th>
                <th>التاريخ</th>
                <th>الأيام</th>
                <th>الخصم</th>
                <th>السبب</th>
                <th>صادر من</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {recentPenaltiesList.map((pen) => (
                <tr key={pen.id}>
                  <td>{resolveUserName(pen.employeeId, getEmployeeById)}</td>
                  <td>{pen.type}</td>
                  <td>{pen.date}</td>
                  <td>{formatPenaltyDays(pen.days)}</td>
                  <td>{pen.deductionLabel || "—"}</td>
                  <td>{pen.reason}</td>
                  <td>
                    <span className="hr-role-tag">
                      {CREATOR_ROLE_LABELS[pen.createdByRole] ||
                        pen.createdByRole}
                    </span>
                  </td>
                  <td>
                    <PenaltyStatusBadge penalty={pen} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {recentPenaltiesList.length === 0 && (
            <p className="hr-empty">لا توجد جزاءات خلال آخر شهرين</p>
          )}
        </div>
      </div>
    );
  }

  function renderComplaints() {
    const sorted = [...complaints].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );

    return (
      <div className="hr-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">جميع الشكاوى</h2>
          <span className="hr-section-count">{complaints.length} شكوى</span>
        </div>
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>العنوان</th>
                <th>مقدم الشكوى</th>
                <th>المستلم</th>
                <th>التاريخ</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div>{c.title}</div>
                    <span className="hr-role-tag">
                      {c.description?.slice(0, 50)}
                      {c.description?.length > 50 ? "…" : ""}
                    </span>
                  </td>
                  <td>
                    <div>{c.senderName}</div>
                    <span className="hr-role-tag">
                      {getRoleLabel(c.senderRole)}
                    </span>
                  </td>
                  <td>
                    <div>{c.receiverName}</div>
                    <span className="hr-role-tag">
                      {getRoleLabel(c.receiverRole)}
                    </span>
                  </td>
                  <td>{formatDate(c.createdAt)}</td>
                  <td>
                    <span
                      className={`hr-badge ${getStatusBadgeClass(c.status)}`}
                    >
                      {getStatusLabel(c.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {complaints.length === 0 && (
            <p className="hr-empty">لا توجد شكاوى مسجلة</p>
          )}
        </div>
      </div>
    );
  }

  function renderAppeals() {
    const sorted = [...appeals].sort(
      (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt),
    );

    return (
      <div className="hr-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">سلسلة التظلمات</h2>
          <span className="hr-section-count">{appeals.length} تظلم</span>
        </div>
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>النوع</th>
                <th>تفاصيل الواقعة</th>
                <th>سبب التظلم</th>
                <th>المستوى الحالي</th>
                <th>تاريخ التقديم</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => {
                const levelKey =
                  a.currentLevel === "department-manager"
                    ? "departmentManager"
                    : a.currentLevel;
                return (
                  <tr key={a.id}>
                    <td>
                      <div>{a.employeeName}</div>
                      <span className="hr-role-tag">{a.employeeCode}</span>
                    </td>
                    <td>{a.type === "penalty" ? "جزاء" : "طلب"}</td>
                    <td>
                      <AppealSubjectDetails
                        appeal={a}
                        penalties={allPenaltiesList}
                        variant="compact"
                      />
                    </td>
                    <td>{a.reason}</td>
                    <td>
                      <span className="hr-role-tag">
                        {getAppealLevelLabel(levelKey, a) || a.currentLevel}
                      </span>
                    </td>
                    <td>{formatDate(a.submittedAt)}</td>
                    <td>
                      <AppealStatusBadge appeal={a} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {appeals.length === 0 && (
            <p className="hr-empty">لا توجد تظلمات مسجلة</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="hr-dashboard" dir="rtl">
      <div className="hr-dash-top">
        <div className="hr-header">
          <h1 className="hr-title">لوحة تحكم الموارد البشرية</h1>
          <p className="hr-subtitle">
            نظرة شاملة على الإجازات والجزاءات والشكاوى والتظلمات في جميع المستويات
          </p>
        </div>
        <HomeAttendanceClock />
      </div>

      <div className="hr-stats-grid">
        <div className="hr-stat-card">
          <span className="hr-stat-icon">👥</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.totalEmployees}</span>
            <span className="hr-stat-label">الموظفون النشطون</span>
          </div>
        </div>
        <div className="hr-stat-card">
          <span className="hr-stat-icon">🏖️</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.onLeaveEmployees}</span>
            <span className="hr-stat-label">في إجازة / راحة</span>
          </div>
        </div>
        <div
          className={`hr-stat-card${stats.pendingLeaveRequests > 0 ? " alert" : ""}`}
        >
          <span className="hr-stat-icon">📋</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.pendingLeaveRequests}</span>
            <span className="hr-stat-label">طلبات إجازة معلقة</span>
          </div>
        </div>
        <div
          className={`hr-stat-card${stats.pendingRequests > 0 ? " alert" : ""}`}
        >
          <span className="hr-stat-icon">⏳</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.pendingRequests}</span>
            <span className="hr-stat-label">طلبات معلقة (الكل)</span>
          </div>
        </div>
        <div className="hr-stat-card">
          <span className="hr-stat-icon">⚠️</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.totalPenalties}</span>
            <span className="hr-stat-label">جزاءات الشهر الحالي</span>
          </div>
        </div>
        <div
          className={`hr-stat-card${stats.openComplaints > 0 ? " alert" : ""}`}
        >
          <span className="hr-stat-icon">📢</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.openComplaints}</span>
            <span className="hr-stat-label">شكاوى مفتوحة</span>
          </div>
        </div>
        <div
          className={`hr-stat-card${stats.hrPendingAppeals > 0 ? " alert" : ""}`}
        >
          <span className="hr-stat-icon">⚖️</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.hrPendingAppeals}</span>
            <span className="hr-stat-label">تظلمات بانتظار HR</span>
          </div>
        </div>
        <div
          className={`hr-stat-card${incompleteProfiles > 0 ? " alert" : ""}`}
        >
          <span className="hr-stat-icon">📁</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{incompleteProfiles}</span>
            <span className="hr-stat-label">ملفات ناقصة</span>
          </div>
        </div>
        <div className="hr-stat-card">
          <span className="hr-stat-icon">🏢</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.totalBranches}</span>
            <span className="hr-stat-label">فروع</span>
          </div>
        </div>
      </div>

      <div className="hr-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`hr-tab${activeTab === tab.id ? " is-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tabBadges[tab.id] > 0 && (
              <span className="hr-tab-badge">{tabBadges[tab.id]}</span>
            )}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && renderOverview()}
      {activeTab === "leaves" && isNavItemVisible(flags, "leaves") && renderLeaves()}
      {activeTab === "penalties" && renderPenalties()}
      {activeTab === "complaints" && isNavItemVisible(flags, "complaints") && renderComplaints()}
      {activeTab === "appeals" && renderAppeals()}
    </div>
  );
}

export default HrDashboard;
